import React, { useState, useEffect } from 'react';
import { useTask } from '../contexts/TaskContext';
import {
    LayoutGrid,
    List,
    FileText,
    Bookmark,
    Folder,
    Clock,
    MoreHorizontal,
    Plus,
    ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SpaceDashboard = ({ spaceId }) => {
    const { projects, folders, getSelectedProject } = useTask();
    const navigate = useNavigate();
    const project = getSelectedProject();
    const [activeTab, setActiveTab] = useState('overview');

    // Filter folders for this space (project)
    // In TaskContext, 'folders' are loaded for the selected project.
    // So if selectedProjectId is set correctly in the page wrapper, 'folders' should be correct.

    if (!project) {
        return <div className="p-8 text-center text-gray-500">Cargando espacio...</div>;
    }

    return (
        <div className="flex-1 flex flex-col h-full bg-gray-50 overflow-y-auto">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center text-sm text-gray-500 mb-4">
                    <span>Espacios</span>
                    <ChevronRight className="w-4 h-4 mx-1" />
                    <span className="font-medium text-gray-900">{project.nombre}</span>
                </div>

                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                        {project.icon && <span className="mr-2">{project.icon}</span>}
                        {project.nombre}
                    </h1>
                    <div className="flex items-center space-x-2">
                        <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                            <span className="text-sm">Agentes</span>
                        </button>
                        <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                            <span className="text-sm">Automatizar</span>
                        </button>
                        <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                            <span className="text-sm">Ask AI</span>
                        </button>
                        <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                            <span className="text-sm">Compartir</span>
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center space-x-1 mt-6 border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'overview'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('lista')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors`}
                    >
                        Lista
                    </button>
                    <button
                        onClick={() => setActiveTab('tablero')}
                        className={`px-4 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors`}
                    >
                        Tablero
                    </button>
                    <button className="p-2 text-gray-400 hover:text-gray-600">
                        <Plus className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Dashboard Content */}
            <div className="p-6 max-w-7xl mx-auto w-full space-y-6">

                {/* Widgets Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                    {/* Recientes */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                        <h3 className="text-sm font-semibold text-gray-900 mb-4">Recientes</h3>
                        <div className="space-y-3">
                            {/* Mock Data */}
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="flex items-start space-x-3 group cursor-pointer hover:bg-gray-50 p-2 rounded-lg -mx-2 transition">
                                    <div className="mt-0.5 text-gray-400 group-hover:text-blue-500">
                                        <List className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600">Gestión de Masterclass</p>
                                        <p className="text-xs text-gray-500">en {project.nombre}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Documentos */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                        <h3 className="text-sm font-semibold text-gray-900 mb-4">Documentos</h3>
                        <div className="flex flex-col items-center justify-center h-32 text-center">
                            <FileText className="w-8 h-8 text-gray-300 mb-2" />
                            <p className="text-sm text-gray-500">No hay documentos recientes</p>
                            <button className="mt-2 text-xs text-blue-600 hover:underline">Crear documento</button>
                        </div>
                    </div>

                    {/* Marcadores */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                        <h3 className="text-sm font-semibold text-gray-900 mb-4">Marcadores</h3>
                        <div className="flex flex-col items-center justify-center h-32 text-center border-2 border-dashed border-gray-100 rounded-lg hover:border-gray-200 transition cursor-pointer group">
                            <div className="p-2 bg-gray-50 rounded-full group-hover:bg-blue-50 transition">
                                <Bookmark className="w-5 h-5 text-gray-400 group-hover:text-blue-500" />
                            </div>
                            <p className="mt-2 text-xs text-gray-500">Agregar marcador</p>
                        </div>
                    </div>
                </div>

                {/* Folders Grid */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <Folder className="w-5 h-5 mr-2 text-gray-400" />
                            Carpetas
                        </h3>
                        <div className="flex space-x-2">
                            <button className="p-1 hover:bg-gray-200 rounded">
                                <LayoutGrid className="w-4 h-4 text-gray-500" />
                            </button>
                            <button className="p-1 hover:bg-gray-200 rounded">
                                <List className="w-4 h-4 text-gray-500" />
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {folders.map((folder) => (
                            <div
                                key={folder.id}
                                className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition cursor-pointer group"
                                onClick={() => {
                                    // Expand folder in sidebar logic? 
                                    // Or navigate to a folder view? 
                                    // For now, maybe just log or do nothing as we don't have a folder page.
                                    // We could navigate to the first list in the folder?
                                }}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-2">
                                        <Folder className="w-5 h-5 text-yellow-500 fill-current" />
                                        <span className="font-medium text-gray-900">{folder.nombre}</span>
                                    </div>
                                    <button className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 rounded">
                                        <MoreHorizontal className="w-4 h-4 text-gray-500" />
                                    </button>
                                </div>
                                <div className="text-xs text-gray-500 mt-2">
                                    {/* We could show number of lists or tasks here if available */}
                                    3 listas
                                </div>
                            </div>
                        ))}

                        {/* Add Folder Card */}
                        <button className="border-2 border-dashed border-gray-200 rounded-xl p-4 flex items-center justify-center text-gray-400 hover:border-blue-300 hover:text-blue-500 transition">
                            <Plus className="w-5 h-5 mr-2" />
                            <span className="font-medium">Nueva carpeta</span>
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default SpaceDashboard;
